"use client";

import { FileText, Minus, Plus } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import * as React from "react";
import { Fragment } from "react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useUI } from "@/components/ui/context";
import { Scroll as ScrollSpy } from "@/components/ui/scroll";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import scrollTo from "@/lib/utils/smooth-scroll";

interface Link {
	title: string;
	href?: string;
	icon?: React.JSX.Element | string;
	isScrollSpy?: boolean;
	component?: React.JSX.Element;
	blockAccess?: boolean;
}

interface ListLeftProps {
	links?: Array<Link>;
}

export default function ListLeft({ links }: ListLeftProps) {
	const { closeMenu } = useUI();

	return (
		<ul
			className={cn(
				"overflow-hidden whitespace-nowrap pt-2 transition-[width] duration-200 lg:pt-0"
			)}
		>
			{links?.map(({ href, title, icon, component, isScrollSpy, blockAccess }: Link) => {
				if (blockAccess) return null;

				if (component) return <li key={title}>{component}</li>;

				if (isScrollSpy) {
					return (
						<li key={title}>
							<ScrollSpy
								to={href}
								spy={true}
								offset={-120}
								smooth={true}
								hashSpy={true}
								duration={200}
								activeClass="isActive-link"
								onClick={() => closeMenu()}
							>
								<div
									className="hover:text-stroke
                  flex w-full cursor-pointer items-start gap-3 rounded px-1.5 py-2 text-gray-900 hover:bg-gray-200/50 hover:text-examine-purple-500 active:bg-gray-200 2xl:gap-3"
								>
									{icon && (
										<Tooltip>
											<TooltipTrigger asChild>
												<span className="grid size-5 shrink-0 place-content-start 3xl:size-6">
													{typeof icon === "string" ? <img src={icon} alt={title} /> : icon}
												</span>
											</TooltipTrigger>
											<TooltipContent variant="purple" className="[@media(max-width:768px)]:hidden">
												{title}
											</TooltipContent>
										</Tooltip>
									)}
									<span className="whitespace-normal text-sm capitalize -tracking-2 peer-checked/left:group-[]:inline-flex lg:hidden xl:text-base 3xl:text-lg 3xl:leading-9">
										{title}
									</span>
								</div>
							</ScrollSpy>
						</li>
					);
				}

				return (
					<li key={title}>
						<Link href={href}>
							<div
								className="hover:text-stroke
                  flex w-full cursor-pointer items-center gap-3 rounded px-1.5 py-2 text-gray-900 hover:bg-gray-200/50 hover:text-examine-purple-500 active:bg-gray-200 2xl:gap-3"
								onClick={() => closeMenu()}
							>
								{icon && (
									<Tooltip>
										<TooltipTrigger asChild>
											<span className="grid size-5 shrink-0 place-content-start 3xl:size-6">
												{typeof icon === "string" ? <img src={icon} alt={title} /> : icon}
											</span>
										</TooltipTrigger>
										<TooltipContent variant="purple" className="[@media(max-width:768px)]:hidden">
											{title}
										</TooltipContent>
									</Tooltip>
								)}
								<span className="whitespace-normal text-sm capitalize -tracking-2 peer-checked/left:group-[]:inline-flex lg:hidden xl:text-base 3xl:text-lg 3xl:leading-9">
									{title}
								</span>
							</div>
						</Link>
					</li>
				);
			})}
		</ul>
	);
}

const linksObject: any = {
	isSummary: {
		title: `Overview`,
		id: `overview`,
		slug: "",
		icon: <img src="/sidebar/summary.png" alt="Overview" />,
	},
	isAllConditions: {
		title: `All Conditions`,
		id: `all-conditions`,
		slug: "",
		icon: <FileText strokeWidth={1} size={22} />,
	},
	isDosage: {
		title: `Dosage Information`,
		id: `dosage-information`,
		slug: "how-to-take",
		icon: <img src="/sidebar/Dosage.png" alt="Dosage" />,
	},
	isSafetyDb: {
		title: `Safety Database`,
		id: `safety-database`,
		slug: `safety-database`,
		icon: <img src="/icons/pro-safety.png" alt="Safety" />,
		subLinks: {
			"safety-overview": "Safety Summary",
			"side-effects": "Side Effects",
			interactions: "Interactions",
			"pregnancy-lactation": "Pregnancy and Lactation",
			precautions: "Precautions",
		},
	},
	isExamineDatabase: {
		title: `Examine Database`,
		id: `examine-database`,
		slug: "database",
		icon: <img src="/sidebar/Examine-Database.png" alt="Database" />,
	},
	isResearchFeed: {
		title: `Research Feed`,
		id: `research-feed`,
		slug: `research-feed`,
		icon: <img src="/sidebar/Feed.png" alt="Research" />,
	},
	isOtherFaqs: {
		title: `Frequently asked questions`,
		id: `frequently-asked-questions`,
		slug: `faq`,
		icon: <img src="/sidebar/FAQ.png" alt="FAQs" />,
	},
	isUpdates: {
		title: `Update History`,
		id: `updates`,
		slug: ``,
		icon: <img src="/sidebar/history.png" alt="Updates" />,
	},
	isResearch: {
		title: `Research Breakdown`,
		id: `research-breakdown`,
		slug: `research`,
		icon: <img src="/sidebar/Research-Breakdown.png" alt="research-breakdown" />,
	},
	isReferences: {
		title: `References`,
		id: `references`,
		slug: ``,
		icon: <img src="/sidebar/References.png" alt="References" />,
	},
};

export const TopicsInterventionsLinks = ({ links, page }: any) => {
	const { closeMenu } = useUI();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const router = useRouter();

	const { type, slug, isSplitTest } = page;
	const searchParamsStr = searchParams.toString();
	const isMainPage = pathname === `/${type}/${slug}/`;
	const pagePath = `/${type}/${slug}`;

	const overviewLinks = links["overviewLinks"];
	const overviewLink = {
		...linksObject.isSummary,
		subLinks: Object.fromEntries(overviewLinks.map(({ link, name }) => [link, name])),
		defaultOpen: true,
	};

	const renderLink = ({ id, slug, icon, title }) => {
		const href = `${pagePath}/${slug}/`;

		const linkProps = {
			className: cn(
				"flex w-full cursor-pointer items-center gap-3 rounded px-1.5 py-2 text-gray-700 hover:bg-gray-200/50 hover:text-examine-purple-500 active:bg-gray-200 2xl:gap-2 2xl:py-2",
				isSplitTest && pathname === href && "bg-examine-purple-100"
			),
		};

		const content = (
			<Fragment key={id}>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="grid size-5 shrink-0 place-content-start 3xl:size-6">
							{typeof icon === "string" ? <img src={icon} alt={title} /> : icon}
						</span>
					</TooltipTrigger>
					<TooltipContent variant="purple" className="[@media(max-width:768px)]:hidden">
						{title}
					</TooltipContent>
				</Tooltip>
				<span className="whitespace-normal text-base capitalize peer-checked/left:group-[]:inline-flex lg:hidden 3xl:text-lg">
					{title}
				</span>
			</Fragment>
		);

		if (isSplitTest) {
			if (!slug) {
				return (
					<Link key={id} href={`${pagePath}/?${searchParamsStr}#${id}`} {...linkProps}>
						{content}
					</Link>
				);
			}

			return (
				<Link key={id} href={`${pagePath}/${slug}/?${searchParamsStr}`} {...linkProps}>
					{content}
				</Link>
			);
		}

		if (!isMainPage) {
			return (
				<Link key={id} href={`${pagePath}/#${id}`} {...linkProps}>
					{content}
				</Link>
			);
		}

		return (
			<ScrollSpy
				key={id}
				to={id}
				spy
				offset={-120}
				smooth
				hashSpy
				duration={200}
				activeClass="isActive-link"
				onClick={() => closeMenu()}
				{...linkProps}
			>
				{content}
			</ScrollSpy>
		);
	};

	const renderSublink = ({ id, title }) => {
		const content = (
			<div className="flex w-full cursor-pointer items-center gap-3 rounded px-1.5 py-1 text-gray-700 hover:bg-gray-200/50 hover:text-examine-purple-500 active:bg-gray-200 2xl:gap-2 2xl:py-2">
				<span className="whitespace-normal capitalize">{title}</span>
			</div>
		);

		if (isSplitTest) {
			return <Link href={`${pathname}?${searchParamsStr}#${id}`}>{content}</Link>;
		}

		if (!isMainPage) {
			return <Link href={`${pagePath}/#${id}`}>{content}</Link>;
		}

		return (
			<ScrollSpy
				to={id}
				spy={true}
				offset={-120}
				smooth={true}
				hashSpy={true}
				duration={200}
				activeClass="isActive-link"
				onClick={() => closeMenu()}
			>
				{content}
			</ScrollSpy>
		);
	};

	const renderCollapsibleLink = ({ id, title, slug, icon, subLinks, defaultOpen }) => {
		const path = slug !== "" ? `${pagePath}/${slug}/` : `${pagePath}/`;
		const isActivePage = pathname === path;
		const renderAsCollapsible = !isSplitTest || isActivePage;

		if (renderAsCollapsible) {
			return (
				<Collapsible key={id} defaultOpen={defaultOpen || isActivePage}>
					<CollapsibleTrigger
						className="group/collapsible w-full font-sans font-normal"
						onClick={() => {
							const toggleElement = document.getElementById("left-toggle") as HTMLInputElement;
							const isCollapsed = toggleElement && !toggleElement.checked;

							if (isCollapsed) {
								const firstId = Object.entries(subLinks)[0][0];

								const to = id === "overview" ? firstId : id;

								const href = isSplitTest
									? `${pathname}?${searchParamsStr}#${to}`
									: !isMainPage
									? `${pagePath}/#${to}`
									: `#${to}`;

								const element = document.getElementById(to);

								if (!element) return;

								element.style.scrollMarginTop = "7rem";

								router.push(href);
								scrollTo(to, { behavior: "smooth", block: "start", inline: "start" });
							}
						}}
					>
						<div
							className={cn(
								"flex w-full cursor-pointer items-center gap-3 rounded px-1.5 py-2 text-gray-700 group-data-[state=open]/collapsible:bg-gray-200/50 group-data-[state=open]/collapsible:text-examine-purple-500 hover:bg-gray-200/50 hover:text-examine-purple-500 active:bg-gray-200 2xl:gap-2 2xl:py-2",
								isSplitTest && "bg-examine-purple-100"
							)}
						>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="grid size-5 shrink-0 place-content-start 3xl:size-6">
										{icon}
									</span>
								</TooltipTrigger>
								<TooltipContent variant="purple" className="[@media(max-width:768px)]:hidden">
									{title}
								</TooltipContent>
							</Tooltip>
							<span className="flex w-full items-center justify-between whitespace-normal text-base capitalize peer-checked/left:group-[]:flex lg:hidden 3xl:text-lg">
								{title}
								<Plus size="14" className="group-data-[state=open]/collapsible:hidden" />
								<Minus size="14" className="group-data-[state=closed]/collapsible:hidden" />
							</span>
						</div>
					</CollapsibleTrigger>
					<CollapsibleContent>
						<ul className="space-y-2.5 pb-6 pl-10 pt-2 peer-checked/left:group-[]:block md:space-y-1 md:pl-8 lg:hidden">
							{Object.entries(subLinks).map(([id, title]) => (
								<li key={id}>{renderSublink({ title, id })}</li>
							))}
						</ul>
					</CollapsibleContent>
				</Collapsible>
			);
		}

		return renderLink({ id, title, icon, slug });
	};

	return (
		<ul
			className={cn(
				"flex flex-col gap-y-2 overflow-hidden whitespace-nowrap pt-2 transition-[width] duration-200 lg:pt-0 2xl:gap-y-1"
			)}
		>
			{overviewLinks?.length > 0 && renderCollapsibleLink(overviewLink)}

			{Object.entries(links)
				?.filter(([_, exists]) => typeof exists === "boolean" && exists)
				?.filter(([link]) => link !== "isSummary" || overviewLinks?.length < 1)
				?.map(([link]) =>
					linksObject[link].subLinks
						? renderCollapsibleLink(linksObject[link])
						: renderLink(linksObject[link])
				)}
		</ul>
	);
};
